<template>
  <a-flex vertical gap="middle" class="chat-wrapper">
    <a-flex align="center" justify="space-between">
      <a-typography-text class="single-req-title">
        {{ currentRequest?.title }}
      </a-typography-text>

      <a-flex align="center" justify="end" gap="middle">
        <a-select
          v-if="userStore.isUserSupport && !!currentRequest?.adminId"
          v-model:value="selectedAdmin"
          :options="supportAdminsOptions"
          :loading="chatStore.isLoadingAdmins"
          :disabled="!canChangeAdmin"
          placeholder="Выберите администратора"
          style="width: 200px"
        />
        <a-button v-if="canCloseRequest" danger @click="closeRequest">
          Закрыть обращение
        </a-button>
        <a-button v-if="canReopenRequest" @click="reopenRequest">
          Открыть обращение заново
        </a-button>
        <a-button v-if="canSolveRequest" @click="solveRequest">
          Отметить обращение как решенное
        </a-button>
      </a-flex>
    </a-flex>

    <div class="messages" ref="scrollContainer" @scroll="onScroll">
      <a-flex gap="middle" vertical v-if="!chatStore.isLoadingMessages">
        <a-spin size="large" v-if="isLoadingMore" />
        <SupportMessage
          v-for="(message, index) in chatStore.currentMessages"
          :key="index"
          :message="message"
          :style="{ alignSelf: message.self ? 'flex-end' : 'flex-start' }"
        />
      </a-flex>
      <a-flex align="center" justify="center" v-else style="width: 100%">
        <a-spin size="large" />
      </a-flex>
    </div>

    <SupportMessageInput
      v-if="!currentRequest?.isClosed || reopenActivated"
      :sendButtonTooltip="'Отправить сообщение'"
      @send="sendClicked"
      v-model:value="message"
      v-model:decodedAttachments="decodedAttachments"
      :is-send-button-disabled="sendButtonDisabled"
    />

    <a-button
      @click="scrollToBottom"
      shape="circle"
      type="dashed"
      class="to-bottom"
      :class="{ visible: showScrollButton }"
    >
      <template #icon>
        <VerticalAlignBottomOutlined />
      </template>
    </a-button>
  </a-flex>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from "vue";
import SupportMessage from "@/components/settings/support/SupportMessage.vue";
import SupportMessageInput from "@/components/settings/support/SupportMessageInput.vue";
import { VerticalAlignBottomOutlined } from "@ant-design/icons-vue";
import { useUser } from "@/stores/user/user";
import { useChatStore } from "@/stores/chat/chat.store";
import type { BlobImageDto } from "@/api/api.dto";

const chatStore = useChatStore();
const userStore = useUser();

const scrollContainer = ref<HTMLElement | null>(null);
const message = ref<string>("");
const showScrollButton = ref(false);
const reopenActivated = ref(false);

const decodedAttachments = ref<BlobImageDto[]>([]);

const isLoadingMore = ref(false);
const isInitialLoad = ref(true);

const sendButtonDisabled = computed(
  () => !message.value.trim().length && !decodedAttachments.value?.length,
);

const currentRequest = computed(() => chatStore.currentRequest);

const canCloseRequest = computed(
  () =>
    (userStore.isUserEmployee || userStore.isUserOwner) &&
    !currentRequest.value?.isClosed,
);

const canReopenRequest = computed(
  () =>
    (userStore.isUserEmployee || userStore.isUserOwner) &&
    currentRequest.value?.isClosed &&
    !reopenActivated.value,
);

const canSolveRequest = computed(
  () =>
    userStore.isUserSupport &&
    !currentRequest.value?.isClosed &&
    !currentRequest.value?.isSolved &&
    !!currentRequest.value?.adminId &&
    currentRequest.value?.adminId === userStore.user?.id,
);

const canChangeAdmin = computed(
  () =>
    userStore.isUserSupport &&
    !currentRequest.value?.isClosed &&
    !currentRequest.value?.isSolved &&
    !!currentRequest.value?.adminId &&
    currentRequest.value?.adminId === userStore.user?.id,
);

const supportAdminsOptions = computed(() =>
  chatStore.supportAdmins.map((admin) => ({
    label: admin.fio || admin.login || admin.id,
    value: admin.id,
  })),
);

const selectedAdmin = computed({
  get: () => currentRequest.value?.adminId,
  set: async (adminId: string) => {
    const chatId = chatStore.currentChatId;
    if (!adminId || !chatId) return;
    await chatStore.changeAdminInChat(chatId, adminId);
    await chatStore.fetchChats();
  },
});

const sendClicked = async () => {
  const chatId = chatStore.currentChatId;
  if (!chatId || sendButtonDisabled.value) return;

  const firstMedia = decodedAttachments.value.at(0);
  const restMedia = firstMedia ? decodedAttachments.value.slice(1) : [];

  await chatStore.addMessageToChatWS({
    message: message.value || null,
    chatId,
    image: firstMedia,
  });

  for (const media of restMedia) {
    await chatStore.addMessageToChatWS({
      message: null,
      chatId,
      image: media,
    });
  }

  message.value = "";
  decodedAttachments.value = [];
  scrollToBottom(200);

  if (reopenActivated.value) {
    reopenActivated.value = false;
    await chatStore.getCurrentRequest(chatId);
    await chatStore.fetchChats();
  } else if (userStore.isUserSupport && !currentRequest.value?.adminId) {
    await chatStore.fetchChats();
  }
};

const closeRequest = async () => {
  if (!canCloseRequest.value) return;

  const chatId = chatStore.currentChatId;
  if (!chatId) return;

  await chatStore.closeChatWS(chatId);
  await chatStore.getCurrentRequest(chatId);
  await chatStore.fetchChats();
};

const solveRequest = async () => {
  if (!canSolveRequest.value) return;

  const chatId = chatStore.currentChatId;
  if (!chatId) return;

  await chatStore.solveChatWS(chatId);
  await chatStore.getCurrentRequest(chatId);
  await chatStore.fetchChats();
};

const reopenRequest = async () => {
  if (!canReopenRequest.value) return;
  reopenActivated.value = true;
  const chatId = chatStore.currentChatId;
  if (!chatId) return;
  await chatStore.openChat(chatId);
};

const onScroll = async () => {
  if (!scrollContainer.value) return;

  const el = scrollContainer.value;
  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
  const distanceFromTop = el.scrollTop;

  showScrollButton.value = distanceFromBottom > 200;

  if (
    distanceFromTop < 100 &&
    distanceFromTop > 0 &&
    !isLoadingMore.value &&
    !isInitialLoad.value &&
    chatStore.hasCurrentChatPreviousPage &&
    !chatStore.isLoadingMessages
  ) {
    isLoadingMore.value = true;

    const scrollHeightBefore = el.scrollHeight;
    const scrollTopBefore = el.scrollTop;

    try {
      const chatId = chatStore.currentChatId;
      if (!chatId) return;

      await chatStore.loadPreviousMessages(chatId);

      // Wait for DOM to update with new messages
      await nextTick();

      const scrollHeightAfter = el.scrollHeight;
      el.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
    } finally {
      isLoadingMore.value = false;
    }
  }
};

const scrollToBottom = (delay: number = 0) => {
  setTimeout(() => {
    if (!scrollContainer.value) return;

    scrollContainer.value.scrollTo({
      top: scrollContainer.value.scrollHeight,
      behavior: "smooth",
    });

    setTimeout(() => {
      isInitialLoad.value = false;
    }, 500);
  }, delay);
};

watch(
  () => chatStore.currentChatId,
  async (chatId: number | null) => {
    if (!chatId) return;
    isInitialLoad.value = true;
    await chatStore.setCurrentChat(chatId);
    scrollToBottom(200);
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.base__content-inner {
  height: 100%;
}

.single-req-title {
  font-size: 15px;
}

.chat-wrapper {
  max-width: 800px;
  height: 500px;
  overflow: hidden;
  position: relative;

  .to-bottom {
    position: absolute;
    right: 25px;
    opacity: 0;
    bottom: 0;
    transition: all 0.3s ease-in-out;

    &.visible {
      bottom: 100px;
      opacity: 1;
    }
  }

  .messages {
    overflow: auto;
    scrollbar-width: thin;
    padding-right: 5px;
  }

  .message-input {
    margin-top: auto;
    z-index: 1;
  }
}
</style>
